import React, { useMemo } from "react";
import { useGame, useMySubmissionsToPublisher } from "@seabrookstudios/pitch2table";
import { Path } from "../Path";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { PublisherStatus } from "../games/PublisherStatus";
import { CoverSquare } from "../games/CoverImages";
import { formatFollowUp, showAlarm } from "@seabrookstudios/pitch2table-core";
import { FollowUpDuePill } from "../games/FollowUpDuePill";
import { getSeverity } from "../games/publisher-interest/follow-up-severity";
import { AddGamesToPublisherDialog } from "./AddGamesToPublisherDialog";
import { idExtractor, useMakeListBuilder, useMakeListView } from "../core/useMakeLayout";
import { NoItemsFound } from "../publisher/NoItemsFound";

const NoSubmissions = () => (
  <NoItemsFound>
    <p>No submissions to this publisher.</p>
  </NoItemsFound>
);

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.item
 */
const MySubmissionListEntry = ({ item }) => {
  const { data: game, isLoading } = useGame(item.gameId);

  const navigate = useNavigate();

  const onClickHandler = () => {
    navigate(Path.game(item.gameId).publisher(item.publisherId));
  };

  return (
    <ListItemButton alignItems="flex-start" onClick={onClickHandler}>
      <ListItemAvatar>
        {isLoading || !game ? <CircularProgress /> : <CoverSquare game={game} width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText primary={game && game.name} secondary={formatFollowUp(item)} />
      {showAlarm(item.nextAction) && <FollowUpDuePill severity={getSeverity(item.nextAction)} />}
      <Box p={0.5} />
      <ListItemIcon>
        <PublisherStatus status={item.status} />
      </ListItemIcon>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const MySubmissionsWithPublisher = ({ publisherId }) => {
  const { data: submissions = [] } = useMySubmissionsToPublisher(publisherId);
  const existingGameIds = useMemo(() => submissions.map((submission) => submission.gameId), [submissions]);

  const Action = () => <AddGamesToPublisherDialog publisherId={publisherId} existingGameIds={existingGameIds} />;

  const useMakeList = useMakeListBuilder(
    MySubmissionListEntry,
    idExtractor,
    "Submissions",
    NoSubmissions,
    Action,
    undefined,
    undefined
  );

  const useWrappedQuery = () => useMySubmissionsToPublisher(publisherId);

  const List = useMakeListView(useWrappedQuery, [], "publisher-submissions", useMakeList);

  return <List />;
};
